import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Grid,
    Link,
    Avatar,
    Tooltip,
    Snackbar
} from '@mui/material';
import { ContentCopyIcon } from 'icons/materialIcons';
import avatarPlaceholder from 'images/avatar-cropped.png';
import { getCardById } from 'apis/cardApi';
import { useAuth } from 'contexts/AuthContext';

const DetailColumn = ({ label, value, onCopy, isLink, linkType, avatar, connectedDate, cardFullName }) => {
    const [showCopyTooltip, setShowCopyTooltip] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    if (!value && !avatar) return null;

    const handleCopy = () => {
        if (onCopy) {
            onCopy(value);
            setShowCopyTooltip(true);
            setTimeout(() => setShowCopyTooltip(false), 2000);
        }
    };

    const renderConnectedInfo = () => (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                cursor: 'pointer',
                width: 'fit-content'
            }}
        >
            <Tooltip
                title={
                    <div>
                        <div>{cardFullName || value}</div>
                        {connectedDate && (
                            <div style={{ fontSize: '12px', marginTop: '4px' }}>
                                Connected on {connectedDate}
                            </div>
                        )}
                    </div>
                }
                placement="right"
                arrow
            >
                <Avatar
                    src={avatar || avatarPlaceholder}
                    sx={{
                        width: 32,
                        height: 32,
                        transition: 'transform 0.2s ease-in-out',
                        '&:hover': {
                            transform: 'scale(1.1)',
                            transformOrigin: 'center'
                        }
                    }}
                />
            </Tooltip>
        </Box>
    );

    const renderValue = () => {
        if (avatar) {
            return renderConnectedInfo();
        }
        if (isLink) {
            return (
                <Link
                    href={linkType === 'email' ? `mailto:${value}` : value}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        color: '#48befa',
                        textDecoration: 'none',
                        '&:hover': {
                            textDecoration: 'underline'
                        }
                    }}
                >
                    {value}
                </Link>
            );
        }
        return (
            <Typography
                variant="body1"
                sx={{
                    color: '#242020',
                    fontSize: '14px',
                    fontWeight: 500,
                    lineHeight: '20px'
                }}
            >
                {value}
            </Typography>
        );
    };

    return (
        <Box sx={{ p: 2, pb: 1, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                    variant="subtitle2"
                    sx={{
                        color: '#828282',
                        mr: 1,
                        fontSize: '13px',
                        fontWeight: 500,
                        lineHeight: '18px'
                    }}
                >
                    {label}
                </Typography>
                {onCopy && value && (
                    <IconButton
                        size="small"
                        onClick={handleCopy}
                        sx={{
                            color: '#828282',
                            p: 0,
                            mr: 1,
                            '&:hover': {
                                backgroundColor: 'transparent'
                            }
                        }}
                    >
                        <ContentCopyIcon sx={{ fontSize: '16px' }} />
                    </IconButton>
                )}
            </Box>
            <Box sx={{ mt: 1 }}>
                {renderValue()}
            </Box>
            <Snackbar
                open={showCopyTooltip}
                message="Copied!"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                sx={{
                    '& .MuiSnackbarContent-root': {
                        bgcolor: 'rgba(0, 0, 0, 0.8)',
                        color: 'white',
                        minWidth: 'auto',
                        padding: '6px 16px',
                        borderRadius: '4px',
                        fontSize: '14px'
                    }
                }}
            />
        </Box>
    );
};

const ContactDetailCard = ({ contact }) => {
    const { authState } = useAuth();
    const [connectedCard, setConnectedCard] = useState(null);

    useEffect(() => {
        const fetchConnectedCard = async () => {
            try {
                let cardId = null;

                // First try to use cardId from contact
                if (contact?.cardId) {
                    cardId = contact.cardId;
                }
                // If no cardId in contact, try to use default card
                else if (authState?.user?.contactCardIds?.length > 0) {
                    // Get default card or first card in the list
                    cardId = authState.user.contactCardIds.find(id =>
                        authState.cardState?.[id]?.is_default
                    ) || authState.user.contactCardIds[0];
                }

                // If we found a cardId, fetch the card details
                if (cardId && authState.token) {
                    // ✅ ENHANCED: Retry logic with exponential backoff for race condition handling
                    let lastError = null;
                    const maxRetries = 3;
                    const baseDelay = 1000; // 1 second base delay

                    for (let attempt = 0; attempt <= maxRetries; attempt++) {
                        try {
                            const response = await getCardById(cardId);
                            if (response && response.card) {
                                setConnectedCard({
                                    name: response.card.contact_info?.fullName || 'Unnamed Card',
                                    avatar: response.card.profilePictureUrl || avatarPlaceholder,
                                    cardId
                                });
                                return; // Success, exit retry loop
                            } else {
                                break; // No valid response, don't retry
                            }
                        } catch (error) {
                            lastError = error;

                            // If this is the last attempt, throw the error
                            if (attempt === maxRetries) {
                                break;
                            }

                            // Calculate delay with exponential backoff
                            const delay = baseDelay * Math.pow(2, attempt);
                            await new Promise(resolve => setTimeout(resolve, delay));
                        }
                    }

                    // If we get here, all retries failed or no valid response
                    // Set fallback info
                    setConnectedCard({
                        name: `${authState?.user?.fullName || 'Your'} Card`,
                        avatar: avatarPlaceholder
                    });
                } else {
                    // If no cardId found, use the user name as fallback
                    setConnectedCard({
                        name: `${authState?.user?.fullName || 'Your'} Card`,
                        avatar: avatarPlaceholder
                    });
                }
            } catch (error) {
                // Set fallback info
                setConnectedCard({
                    name: `${authState?.user?.fullName || 'Your'} Card`,
                    avatar: avatarPlaceholder
                });
            }
        };

        fetchConnectedCard();
    }, [contact, authState]);

    const handleCopy = (value) => {
        if (value) {
            navigator.clipboard.writeText(value);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return null;
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    // Process all fields into a single array, with Connected With at the end
    const getAllFields = () => {
        const standardFields = [];
        let connectedWithField = null;
        const customFieldsArray = [];

        // Add standard fields first
        if (contact?.leadName) standardFields.push({
            label: "Name",
            value: contact.leadName,
            onCopy: handleCopy
        });

        if (contact?.leadEmail) standardFields.push({
            label: "Email",
            value: contact.leadEmail,
            onCopy: handleCopy,
            isLink: true,
            linkType: "email"
        });

        if (contact?.leadPhoneNumber) standardFields.push({
            label: "Number",
            value: contact.leadPhoneNumber,
            onCopy: handleCopy
        });

        if (contact?.leadJobTitle) standardFields.push({
            label: "Job Title",
            value: contact.leadJobTitle,
            onCopy: handleCopy
        });

        if (contact?.leadCompany) standardFields.push({
            label: "Company",
            value: contact.leadCompany,
            onCopy: handleCopy
        });

        if (contact?.createdAt) standardFields.push({
            label: "Date",
            value: formatDate(contact.createdAt),
            onCopy: handleCopy
        });

        if (contact?.linkedin) standardFields.push({
            label: "LinkedIn",
            value: contact.linkedin,
            onCopy: handleCopy,
            isLink: true
        });

        if (contact?.website) standardFields.push({
            label: "Website",
            value: contact.website,
            onCopy: handleCopy,
            isLink: true
        });

        if (contact?.leadAddress) standardFields.push({
            label: "Location",
            value: contact.leadAddress,
            onCopy: handleCopy
        });

        if (contact?.leadNotes) standardFields.push({
            label: "Note",
            value: contact.leadNotes,
            onCopy: handleCopy
        });

        // Process custom fields
        if (contact?.customFields && typeof contact.customFields === 'object') {
            // Handle both Map and regular object structures
            const isMapType = contact.customFields instanceof Map ||
                (typeof contact.customFields.get === 'function');

            if (isMapType) {
                // Convert Map to array
                for (const [key, field] of contact.customFields.entries()) {
                    const fieldValue = typeof field === 'object' ? field.value : field;
                    if (fieldValue && fieldValue.toString().trim()) {
                        customFieldsArray.push({
                            label: key,
                            value: fieldValue,
                            onCopy: handleCopy
                        });
                    }
                }
            } else {
                // Process as regular object
                Object.entries(contact.customFields).forEach(([key, field]) => {
                    // Check if field is an object with a value property (common format)
                    const fieldValue = typeof field === 'object' && field !== null ? field.value : field;
                    const fieldLabel = typeof field === 'object' && field !== null && field.label ? field.label : key;

                    // Only add non-empty fields
                    if (fieldValue && fieldValue.toString().trim()) {
                        customFieldsArray.push({
                            label: fieldLabel,
                            value: fieldValue,
                            onCopy: handleCopy
                        });
                    }
                });
            }
        }

        // Add connected with field (to be placed last)
        if (connectedCard) {
            connectedWithField = {
                label: "Connected with",
                value: connectedCard.name,
                avatar: connectedCard.avatar,
                cardFullName: connectedCard.name,
                onCopy: handleCopy,
                connectedDate: formatDate(contact?.connectedDate || contact?.createdAt || new Date())
            };
        }

        // Combine all fields in order (standard, custom, connected last)
        const allFields = [...standardFields, ...customFieldsArray];
        if (connectedWithField) {
            allFields.push(connectedWithField);
        }

        return allFields;
    };

    const allFields = getAllFields();

    return (
        <Box sx={{ mt: 3, overflow: 'hidden' }}>
            <Grid container>
                {allFields.map((field, index) => (
                    <Grid
                        item
                        xs={12}
                        sm={field.label === 'Connected with' || field.label === 'Note' ? 12 : 6}
                        key={index}
                    >
                        <DetailColumn {...field} />
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default ContactDetailCard;